import { getFeatureFlags } from '@app/services/feature-flags.service'
import { BehaviorSubject, firstValueFrom } from 'rxjs'

/**
 * Feature flag names, mirroring the backend FeatureFlagConstants
 * (src/Core/Constants/FeatureFlagConstants.cs). Flags are configured
 * server-side under the FeatureManagement configuration section.
 */
export const FeatureFlag = {
  Marketplace: 'Marketplace',
  TechConnect: 'TechConnect',
} as const

export type FeatureFlagName = (typeof FeatureFlag)[keyof typeof FeatureFlag]

// Enabled feature flags subject which starts empty (fail closed: until the
// flags are loaded, every gated feature is treated as disabled)
const $enabledFeatures = new BehaviorSubject<string[]>([])

// Enabled feature flags observable for subscriptions
const $enabledFeaturesObservable = $enabledFeatures.asObservable()

/**
 * Get enabled feature flags observable to listen for changes
 * @returns Observable
 */
export const getEnabledFeaturesObservable = () => $enabledFeaturesObservable

/**
 * Get currently enabled feature flags
 * @returns Promise
 */
export const getEnabledFeatures = () => firstValueFrom(getEnabledFeaturesObservable())

/**
 * Check whether a feature flag is enabled
 * @param feature
 * @returns Promise
 */
export const isFeatureEnabled = async (feature: FeatureFlagName) => {
  const enabledFeatures = await getEnabledFeatures()
  return enabledFeatures.includes(feature)
}

/**
 * Load feature flags from the backend
 */
export const loadFeatureFlags = async () => {
  const { data } = await getFeatureFlags(true)
  if (data) {
    $enabledFeatures.next(data.enabledFeatures)
  }
}
